import { useState } from "react";

import {
  OPTION_LABEL_BY_VALUE,
  PREVIOUS_YEAR_SEGMENTS,
  resolveDateRange,
  resolvePreviousYearRange,
} from "@shared/v2/DateRangeDropdown";

import TeamLeaderboard from "./TeamLeaderboard";
import GlobalLeaderboard from "./GlobalLeaderboard";
import TeamTable from "./TeamTable";
import Header from "./Header";
import BigQueryWarning from "./BigQueryWarning";
import { CurrentYear } from "./helpers";
import useFullscreen from "../BusinessDashboard/hooks/useFullscreen";
import useStateWithPersistence from "./hooks/useStateWithPersistence";
import useLeaderboard from "./hooks/useLeaderboard";

const DEFAULT_STANDARD_VALUE = "this_month";
const DEFAULT_PREVIOUS_YEAR_SEGMENT = "january";
const REPORT_CONFIG = {
  disableFutureDates: false,
  excludeOptions: ["all_time"],
  defaultOption: DEFAULT_STANDARD_VALUE,
};
// Derived from REPORT_CONFIG so ranges seeded here can't drift from the ones
// the dropdown emits.
const RESOLVER_OPTIONS = { disableFutureDates: REPORT_CONFIG.disableFutureDates };
const PREVIOUS_YEAR_LABEL_BY_VALUE = PREVIOUS_YEAR_SEGMENTS.reduce(
  (acc, { value, label }) => ({ ...acc, [value]: label }),
  {},
);

const Leaderboards = ({ currentUserId, isPlaceUser }) => {
  const [selectedUserId, setSelectedUserId] = useState(currentUserId);
  const [selectedActivity, setSelectedActivity] = useStateWithPersistence("activity", "outbound_calls");
  const [year, setYear] = useStateWithPersistence("year", CurrentYear);
  const [standardValue, setStandardValue] = useStateWithPersistence(
    "timeFrameV2",
    DEFAULT_STANDARD_VALUE,
  );
  const [previousYearSegment, setPreviousYearSegment] = useStateWithPersistence(
    "previousYearSegmentV2",
    DEFAULT_PREVIOUS_YEAR_SEGMENT,
  );
  const [customRange, setCustomRange] = useStateWithPersistence("customRangeV2", {
    startAt: null,
    endAt: null,
  });

  const isPreviousYear = year !== CurrentYear;

  // Seed dateRange so the initial `useLeaderboard` fetch matches what the
  // `DateRangeDropdown` will emit on mount.
  const [dateRange, setDateRange] = useState(() => {
    if (isPreviousYear) return resolvePreviousYearRange(year, previousYearSegment);
    if (standardValue === "custom") return customRange;
    return resolveDateRange(standardValue, RESOLVER_OPTIONS);
  });

  const { ref, toggleFullscreen } = useFullscreen();

  const handleStandardChange = (value, resolvedRange, meta) => {
    setStandardValue(value);
    // Skip partial Custom ranges: freezing on the last-valid range keeps the
    // report visible while the user finishes editing.
    if (!meta.isValid) return;
    // Trust the range the dropdown resolved: re-resolving here risks drifting
    // from `REPORT_CONFIG` (that drift is what made `last_N_days` return N-1
    // days once the shared resolver was corrected).
    setDateRange(resolvedRange);
    if (value === "custom") {
      setCustomRange(resolvedRange);
    }
  };

  const handlePreviousYearChange = (value, resolvedRange) => {
    setPreviousYearSegment(value);
    setDateRange(resolvedRange);
  };

  const timeFrameLabel = isPreviousYear
    ? `${PREVIOUS_YEAR_LABEL_BY_VALUE[previousYearSegment]} ${year}`
    : OPTION_LABEL_BY_VALUE[standardValue];

  const abandonCustomQuery = () => {
    // Reset to default - "this month"
    setYear(CurrentYear);
    setStandardValue(DEFAULT_STANDARD_VALUE);
    setDateRange(resolveDateRange(DEFAULT_STANDARD_VALUE, RESOLVER_OPTIONS));
  };

  const {
    data: { goalDivisor, largeQuery, maxCount, ranks, users },
    loading,
    rankLoading,
  } = useLeaderboard({
    activity: selectedActivity,
    userId: selectedUserId,
    from: dateRange.startAt,
    to: dateRange.endAt,
  });

  return (
    <div ref={ref} className="leaderboards tw-bg-gray-5 tw-flex tw-p-[16px]">
      <div className="tw-w-full tw-max-w-[1336px] tw-mx-auto tw-flex tw-flex-col tw-gap-[16px]">
        {largeQuery === true && loading === true ? (
          <BigQueryWarning
            customRange={dateRange}
            abandonCustomQuery={abandonCustomQuery}
          />
        ) : (
          ""
        )}
        <Header
          year={year}
          setYear={setYear}
          isPreviousYear={isPreviousYear}
          standardValue={standardValue}
          previousYearSegment={previousYearSegment}
          customRange={customRange}
          reportConfig={REPORT_CONFIG}
          onStandardChange={handleStandardChange}
          onPreviousYearChange={handlePreviousYearChange}
          setFullscreen={toggleFullscreen}
        />
        <div className="tw-flex tw-items-stretch tw-flex-wrap tw-gap-[16px]">
          <GlobalLeaderboard
            wasActivityInPeriod={wasActivityInPeriod(ranks)}
            selectedActivity={selectedActivity}
            ranks={ranks}
            isPlaceUser={isPlaceUser}
            isLoading={loading || rankLoading}
            users={users} // Consider a separate state piece as this won't need to change on sort
            setSelectedUser={setSelectedUserId}
            selectedUser={selectedUserId}
          />
          <TeamLeaderboard
            users={users}
            selectedActivity={selectedActivity}
            isPlaceUser={isPlaceUser}
            isLoading={loading}
          />
        </div>
        <TeamTable
          users={users}
          maxCount={maxCount}
          selectedActivity={selectedActivity}
          setSelectedActivity={setSelectedActivity}
          goalDivider={goalDivisor}
          selectedUser={selectedUserId}
          isPlaceUser={isPlaceUser}
          isLoading={loading}
          timeFrame={timeFrameLabel}
        />
      </div>
    </div>
  );
};

export default Leaderboards;

const wasActivityInPeriod = (ranks) => {
  return ranks.teamRank !== null && ranks.teamRank !== undefined;
};
